import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import Pagination from '../../components/Pagination';
import { StarIcon } from 'lucide-react';
import { PhotoIcon, FaceSmileIcon } from '@heroicons/react/24/outline';
import ReviewCard from '../../components/ReviewCard';
import ReviewForm from '../../components/ReviewForm';

export default function StorePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 1
  });
  const [reviews, setReviews] = useState([]);
  const [reviewPagination, setReviewPagination] = useState({
    page: 1,
    limit: 5,
    total: 0,
    pages: 1
  });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedProductForReview, setSelectedProductForReview] = useState(null);

  const fetchStoreReviews = useCallback(async (page = 1) => {
  try {
    const params = {
      page,
      limit: reviewPagination.limit
    };

    const { data } = await api.get(`/reviews/store/${store._id}`, { params });
    setReviews(data.data);
    setReviewPagination({
      ...data.pagination,
      page: parseInt(data.pagination.page, 10)
    });
  } catch (err) {
    console.error('Error fetching reviews:', err);
  }
}, [store?._id, reviewPagination.limit]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (store?._id) {
      fetchStoreReviews();
    }
  }, [store]);

  useEffect(() => {
    const fetchStoreData = async () => {
      try {
        setLoading(true);
        
        // Fetch store details
        const storeResponse = await api.get(`/stores/${slug}`);
        setStore(storeResponse.data.data.store);
        
        // Fetch products for this store
        await fetchProducts(storeResponse.data.data.store._id);
      } catch (err) {
        console.error('Error fetching store data:', err);
        setError('Failed to load store data');
      } finally {
        setLoading(false);
      }
    };

    fetchStoreData();
  }, [slug, fetchProducts]);

  const fetchProducts = async (storeId, page = 1) => {
    try {
      const params = {
        page,
        limit: pagination.limit,
        store: storeId
      };

      const { data } = await api.get('/products', { params });
      setProducts(data.data.products);
      setPagination({
        ...data.data.pagination,
        page: parseInt(data.data.pagination.page, 10)
      });
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage !== pagination.page && store) {
      fetchProducts(store._id, newPage);
    }
  };

  

  const handleAddReviewClick = (product) => {
    if (!product) {
      console.error('Product not available for review');
      return;
    }
    setSelectedProductForReview(product);
    setShowReviewForm(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin h-10 w-10 border-4 border-pink-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-700">{error}</h2>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-700">Store not found</h2>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Store Banner */}
      <div 
        className="relative h-64 w-full bg-gradient-to-r from-purple-600 to-pink-500 flex items-center justify-center"
        style={{
          backgroundColor: store.theme?.primaryColor || '#7C3AED',
          backgroundImage: store.banner ? `url(${store.banner})` : 'none'
        }}
      >
        {!store.banner && (
          <h1 className="text-white text-4xl font-bold z-10">{store.name}</h1>
        )}
        <div className="absolute inset-0 bg-black bg-opacity-30" />
      </div>

      {/* Store Info */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
          {store.logo ? (
            <img 
              src={store.logo} 
              alt="logo" 
              className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-md" 
            />
          ) : (
            <div 
              className="h-24 w-24 text-white flex items-center justify-center rounded-full text-3xl font-bold shadow-md"
              style={{ backgroundColor: store.theme?.primaryColor || '#7C3AED' }}
            >
              {store.name.charAt(0)}
            </div>
          )}
          <div className="flex-1">
            <h2 className="text-2xl font-semibold text-gray-900">{store.name}</h2>
            {store.description && (
              <p className="text-gray-700 mt-2 max-w-3xl">{store.description}</p>
            )}
            
            {/* Social Links */}
            {(store.socialLinks?.instagram || store.socialLinks?.twitter) && (
              <div className="flex space-x-4 mt-3">
                {store.socialLinks.instagram && (
                  <a 
                    href={
                      store.socialLinks.instagram?.startsWith('http')
                        ? store.socialLinks.instagram
                        : `https://instagram.com/${store.socialLinks.instagram}`
                    }
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-pink-600 transition-colors"
                  >
                    <span className="sr-only">Instagram</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                    </svg>
                  </a>
                )}
                {store.socialLinks.twitter && (
                  <a 
                    href={
                      store.socialLinks.twitter?.startsWith('http')
                        ? store.socialLinks.twitter
                        : `https://x.com/${store.socialLinks.twitter}`
                    }
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-blue-400 transition-colors"
                  >
                    <span className="sr-only">Twitter</span>
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Store Stats */}
      <div className="bg-white py-6 border-t border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
            <div className="p-4">
              <p className="text-2xl font-bold" style={{ color: store.theme?.primaryColor }}>
                {store.stats?.totalProducts || 0}
              </p>
              <p className="text-sm text-gray-600">Products</p>
            </div>
            <div className="p-4">
              <p className="text-2xl font-bold" style={{ color: store.theme?.primaryColor }}>
                {store.stats?.totalOrders || 0}
              </p>
              <p className="text-sm text-gray-600">Orders</p>
            </div>
            <div className="p-4">
              <p className="text-2xl font-bold" style={{ color: store.theme?.primaryColor }}>
                {store.stats?.totalSales || 0}
              </p>
              <p className="text-sm text-gray-600">Sales</p>
            </div>
            <div className="p-4">
              <p className="text-2xl font-bold" style={{ color: store.theme?.primaryColor }}>
                ₹{store.stats?.totalEarnings || 0}
              </p>
              <p className="text-sm text-gray-600">Earnings</p>
            </div>
            <div className="p-4">
              <p className="text-2xl font-bold" style={{ color: store.theme?.primaryColor }}>
                {store.stats?.averageRating ? store.stats.averageRating.toFixed(1) : '0.0'}
              </p>
              <p className="text-sm text-gray-600">Rating</p>
            </div>
            <div className="p-4">
              <p className="text-2xl font-bold" style={{ color: store.theme?.primaryColor }}>
                {store.stats?.totalReviews || 0}
              </p>
              <p className="text-sm text-gray-600">Reviews</p>
            </div>
          </div>
        </div>
      </div>

      {/* Product List */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-800">Products</h3>
          {products.length > 0 && (
            <div className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * pagination.limit + 1}-
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} products
            </div>
          )}
        </div>

        {products.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No products found</h3>
            <p className="mt-1 text-gray-500">This store hasn't added any products yet.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {products.map((product) => {
                const imageUrl = product?.design?.image;
                const isValidImage = imageUrl && imageUrl.startsWith("http");
                const fallbackImage = "/default-product.jpg";

                return (
                  <div
                    key={product._id}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  >
                    <div 
                      className="h-48 bg-gray-100 relative cursor-pointer"
                      onClick={() => navigate(`/store/${store.slug}/products/${product._id}`)}
                    >
                      <img
                        src={isValidImage ? imageUrl : fallbackImage}
                        alt={product.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h4 
                        className="text-lg font-semibold text-gray-900 truncate cursor-pointer"
                        onClick={() => navigate(`/store/${store.slug}/products/${product._id}`)}
                      >
                        {product.name}
                      </h4>
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">{product.description}</p>
                      <div className="mt-3 flex justify-between items-center">
                        <span className="text-lg font-bold" style={{ color: store.theme?.primaryColor }}>
                          ₹{product.variants?.[0]?.finalPrice || product.price || 0}
                        </span>
                        <button
                          className="px-3 py-1 rounded-md text-sm font-medium text-white hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: store.theme?.primaryColor }}
                          onClick={() => navigate(`/store/${store.slug}/products/${product._id}`)}
                        >
                          View Product
                        </button>
                      </div>
                      <button
                        className="mt-2 w-full text-sm text-center text-pink-600 hover:text-pink-800"
                        onClick={() => handleAddReviewClick(product)}
                      >
                        Write a Review
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {pagination.pages > 1 && (
              <div className="flex justify-center mt-8">
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.pages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Reviews Section */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Customer Reviews</h3>
            <div className="flex items-center space-x-4">
              {reviews.length > 0 && (
                <div className="flex items-center">
                  <StarIcon className="h-5 w-5 text-yellow-400" />
                  <span className="ml-1 text-gray-700">
                    {store.stats?.averageRating?.toFixed(1) || '0.0'} ({store.stats?.totalReviews || 0} reviews)
                  </span>
                </div>
              )}
              <button
                onClick={() => setShowReviewForm(true)}
                className="px-3 py-1 text-sm rounded-md text-white"
                style={{ backgroundColor: store.theme?.primaryColor }}
              >
                Write a Review
              </button>
            </div>
          </div>

          {reviews.length === 0 ? (
            <div className="text-center py-8">
              <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">No reviews yet</h3>
              <p className="mt-1 text-gray-500">Be the first to review products from this store!</p>
              <button
                onClick={() => setShowReviewForm(true)}
                className="mt-4 px-4 py-2 rounded-md text-white"
                style={{ backgroundColor: store.theme?.primaryColor }}
              >
                Write a Review
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-6">
                {reviews.map((review) => (
                  <ReviewCard 
                    key={review._id} 
                    review={review} 
                    onProductClick={() => navigate(`/store/${store.slug}/products/${review.product._id}`)}
                  />
                ))}
              </div>

              {reviewPagination.pages > 1 && (
                <div className="flex justify-center mt-8">
                  <Pagination
                    currentPage={reviewPagination.page}
                    totalPages={reviewPagination.pages}
                    onPageChange={(newPage) => fetchStoreReviews(newPage)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Review Form Modal */}
      {showReviewForm && (
    <ReviewForm
      product={selectedProductForReview || products[0]} // Fallback to first product if none selected
      store={store}
      onClose={() => {
        setShowReviewForm(false);
        setSelectedProductForReview(null);
      }}
      onSubmit={() => {
        fetchStoreReviews();
        setShowReviewForm(false);
        setSelectedProductForReview(null);
      }}
    />
  )}
    </div>
  );
}